import type { LatLngTuple } from "@fortex/api";

export const earthRadiusMeters = 6371008.8;

export function radians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function degrees(value: number): number {
  return (value * 180) / Math.PI;
}

export function distanceMeters(from: LatLngTuple, to: LatLngTuple): number {
  const latFrom = radians(from[0]);
  const latTo = radians(to[0]);
  const deltaLat = radians(to[0] - from[0]);
  const deltaLon = radians(to[1] - from[1]);
  const haversine =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) + Math.cos(latFrom) * Math.cos(latTo) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

export function lineLengthMeters(points: LatLngTuple[]): number {
  let total = 0;

  for (let index = 1; index < points.length; index++) total += distanceMeters(points[index - 1], points[index]);

  return total;
}

export function ringPerimeterMeters(points: LatLngTuple[]): number {
  if (points.length < 2) return 0;

  const first = points[0];
  const last = points[points.length - 1];
  const closed = first[0] === last[0] && first[1] === last[1];

  return lineLengthMeters(points) + (closed ? 0 : distanceMeters(last, first));
}

export function ringAreaSquareMeters(points: LatLngTuple[]): number {
  if (points.length < 3) return 0;

  let sum = 0;

  for (let index = 0; index < points.length; index++) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    const lonCurrent = radians(current[1]);
    const lonNext = radians(next[1]);
    const latCurrent = radians(current[0]);
    const latNext = radians(next[0]);

    sum += (lonNext - lonCurrent) * (2 + Math.sin(latCurrent) + Math.sin(latNext));
  }

  return Math.abs((sum * earthRadiusMeters * earthRadiusMeters) / 2);
}

export function midpoint(from: LatLngTuple, to: LatLngTuple): LatLngTuple {
  return [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
}

export function bearingDegrees(from: LatLngTuple, to: LatLngTuple): number {
  const latFrom = radians(from[0]);
  const latTo = radians(to[0]);
  const deltaLon = radians(to[1] - from[1]);
  const y = Math.sin(deltaLon) * Math.cos(latTo);
  const x = Math.cos(latFrom) * Math.sin(latTo) - Math.sin(latFrom) * Math.cos(latTo) * Math.cos(deltaLon);

  return (degrees(Math.atan2(y, x)) + 360) % 360;
}

export function destinationPoint(from: LatLngTuple, bearing: number, meters: number): LatLngTuple {
  const angular = meters / earthRadiusMeters;
  const heading = radians(bearing);
  const latFrom = radians(from[0]);
  const lonFrom = radians(from[1]);
  const latTo = Math.asin(Math.sin(latFrom) * Math.cos(angular) + Math.cos(latFrom) * Math.sin(angular) * Math.cos(heading));
  const lonTo = lonFrom + Math.atan2(Math.sin(heading) * Math.sin(angular) * Math.cos(latFrom), Math.cos(angular) - Math.sin(latFrom) * Math.sin(latTo));

  return [degrees(latTo), ((degrees(lonTo) + 540) % 360) - 180];
}

const locale = "hu-HU";

export function lengthText(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return "-";

  if (meters < 1000) return `${Math.round(meters).toLocaleString(locale)} m`;

  return `${(meters / 1000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 })} km`;
}

export function areaText(squareMeters: number | null | undefined): string {
  if (squareMeters === null || squareMeters === undefined || !Number.isFinite(squareMeters)) return "-";

  return `${Math.round(squareMeters).toLocaleString(locale)} m²`;
}

export function coordinateText(value: number, digits = 5): string {
  return value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function pairText(point: LatLngTuple, digits = 5): string {
  return `${coordinateText(point[0], digits)}, ${coordinateText(point[1], digits)}`;
}
